
import { api } from "@/services/Api";
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  computed,
  onActivated,
  nextTick,
  ComponentPublicInstance,
} from "vue";
import Dropdown from "primevue/dropdown";
import swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import {
  Order,
  Customer,
  ReservedOrder,
  OrderPayment,
  CashPayment,
  CheckPayment,
  BankTransferPayment,
  CreditCardPayment,
  OrderFilter,
} from "@/models/Order";
import moment from "moment";
import InputText from "primevue/inputtext";
import { useStore } from "@/store";
import { useRouter, useRoute } from "vue-router";
import TextInput from "@/components/Inputs/TextInput.vue";
import { BusinessClient } from "@/models/Order";
import { OrderSearchType, OrderStatus, PaymentType } from "@/models/Enums";
import q from "q";
import { Coupon, IdName } from "@/models/Product";
import RadioInput from "@/components/Inputs/RadioInput.vue";
import DateInput from "@/components/Inputs/DateInput.vue";
import _ from "lodash";

export default defineComponent({
  name: "ReservedOrderSearch",
  components: { InputText, TextInput, Dropdown, RadioInput, DateInput },
  beforeRouteEnter(to, from, next) {
    if (from.name == "home") {
      to.params.fromHome = "1";
    }
    next();
  },
  async setup() {
    const route = useRoute();
    const searchBy = ref(OrderSearchType.OrderNumber);
    const loaded = ref(false);
    const store = useStore();
    const orderFilter = ref(new OrderFilter());
    orderFilter.value = store.state.orderFilter;
    const searchWas = ref(false);
    const busy = ref(false);
    const hasMore = ref(true);
    const showNoResult = ref(false);
    const router = useRouter();
    const clients = ref<BusinessClient[]>([]);
    const products = ref<IdName[]>([]);
    const { t } = useI18n();
    const searchInput = ref<ComponentPublicInstance | null>(null);
    const coupons = computed(() => store.state.coupons as Coupon[]);
    const filterExist = computed(() => {
      if (orderFilter.value.orderNumber) {
        return true;
      }
      if (orderFilter.value.keywords) {
        return true;
      }
      if (orderFilter.value.customerName) {
        return true;
      }
      if (orderFilter.value.mobile) {
        return true;
      }
      if (orderFilter.value.pnr) {
        return true;
      }
      if (orderFilter.value.businessClientId) {
        return true;
      }
      if (orderFilter.value.groupName) {
        return true;
      }
      return false;
    });
    const orders = ref<ReservedOrder[]>([]);
    function createDebounce() {
      let timeout = 0;
      return function (fnc: any, delayMs?: number) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          fnc();
        }, delayMs || 1200);
      };
    }
    async function loadMore() {
      await search();
    }

    async function onChangeKeyword() {
      if (orderFilter.value.keywords.length < 2) {
        return;
      }
      await onClickSearch();
    }
    async function onClickSearch() {
      var valid = validateFilter();
      if (!valid) {
        return;
      }
      orders.value = [];
      orderFilter.value.pageIndex = 0;
      hasMore.value = true;
      store.commit("clearOrder");
      await search();
      if (orders.value.length == 0) {
        showNoResult.value = true;
      } else {
        showNoResult.value = false;
        searchWas.value = true;
      }
    }
    async function onClickSearchBy() {
      var valid = validateFilter();
      if (!valid) {
        return;
      }
      orders.value = [];
      orderFilter.value.pageIndex = 0;
      hasMore.value = true;
      store.commit("clearOrder");
      await searchByOrderNumberOrTicket();
      if (orders.value.length == 0) {
        showNoResult.value = true;
      } else {
        showNoResult.value = false;
        searchWas.value = true;
      }
    }
    function containsOnlyNumbers(str) {
      return /^\d+$/.test(str.trim());
    }
    function validateFilter() {
      if (orderFilter.value.keywords && orderFilter.value.keywords.length < 2) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: t("cashier.search.keywords-length"),
        });
        return false;
      }
      if (orderFilter.value.searchValue && orderFilter.value.searchValue.length < 2) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: t("cashier.search.keywords-length"),
        });
        return false;
      }
      if (
        orderFilter.value.orderNumber &&
        !containsOnlyNumbers(orderFilter.value.orderNumber)
      ) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: t("cashier.search.order-number-only-numbers"),
        });
        return false;
      }
      if (
        orderFilter.value.searchValue &&
        !containsOnlyNumbers(orderFilter.value.searchValue) &&
        !/[ZT]/.test(orderFilter.value.searchValue) // Check for 'Z' or 'T'
      ) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: t("cashier.search.order-number-only-numbers"),
        });
        return false;
      }
      if (
        orderFilter.value.customerName &&
        orderFilter.value.customerName.length < 2
      ) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: t("cashier.search.customer-length"),
        });
        return false;
      }
      if (orderFilter.value.pnr && orderFilter.value.pnr.length < 3) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: t("cashier.search.ext-sys-number-length"),
        });
        return false;
      }
      if (orderFilter.value.groupName && orderFilter.value.groupName.length < 2) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: t("cashier.search.groupName-length"),
        });
        return false;
      }
      return true;
    }
    async function search() {
      if (busy.value) {
        return;
      }
      var valid = validateFilter();
      if (!valid) {
        return;
      }
      swal.showLoading();
      busy.value = true;
      var apiResultPromise = api.SearchReservedOrders(orderFilter.value);
      await q.delay(400);
      var apiResult = await apiResultPromise;
      busy.value = false;
      if (apiResult.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: apiResult.errorMessage,
        });
        return;
      }
      if (!apiResult.data) {
        swal.close();
        return;
      }
      orderFilter.value.pageIndex++;      
      if (apiResult.data.orders.length < 50) {
        hasMore.value = false;
      }
      if (orderFilter.value.pageIndex > 0) {
        orders.value = [...orders.value, ...apiResult.data.orders];
      } else {
        orders.value = apiResult.data.orders;
      }
      swal.close();
    }
    async function searchByOrderNumberOrTicket() {
      if (busy.value) {
        return;
      }
      var valid = validateFilter();
      if (!valid) {
        return;
      }
      swal.showLoading();
      busy.value = true;
      var apiResultPromise = api.SearchReservedOrdersBy(orderFilter.value);
      await q.delay(400);
      var apiResult = await apiResultPromise;
      busy.value = false;
      if (apiResult.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: apiResult.errorMessage,
        });
        return;
      }
      if (!apiResult.data) {
        swal.close();
        return;
      }
      orderFilter.value.pageIndex++;
      
      if(apiResult.data.orders.length == 1){
        showOrderDetails(apiResult.data.orders[0].id);
        return;
      }
      if (apiResult.data.orders.length < 50) {
        hasMore.value = false;
      }
      if (orderFilter.value.pageIndex > 0) {
        orders.value = [...orders.value, ...apiResult.data.orders];
      } else {
        orders.value = apiResult.data.orders;
      }
      swal.close();
    }
    function getDateToTitle(date: string) {
      const newDate = new Date(date);
      return moment(newDate).format("DD/MM/YYYY");
    }
    async function handleScroll() {
      if (!hasMore.value) {
        return;
      }
      const orders = document.getElementById("list-orders");
      if (!orders) {
        return;
      }
      //   console.log(
      //     orders.scrollTop + orders.clientHeight + " --- " + orders.scrollHeight
      //   );
      if (orders.scrollTop + orders.clientHeight >= orders.scrollHeight - 5) {
        // console.log("loadMore");
        await loadMore();
      }
    }
    function clearFilter() {
      orderFilter.value.pageIndex = 0;
      orderFilter.value.orderNumber = "";
      orderFilter.value.keywords = "";
      orderFilter.value.customerName = "";
      orderFilter.value.mobile = "";
      orderFilter.value.pnr = "";
      orderFilter.value.businessClientId = null;
    }
    function back() {
      if (searchWas.value) {
        searchWas.value = !searchWas.value;
        return;
      }
      if (orderFilter.value.advancedSearch) {
        orderFilter.value.orderNumber = "";
        orderFilter.value.keywords = "";
        orderFilter.value.customerName = "";
        orderFilter.value.mobile = "";
        orderFilter.value.pnr = "";
        orderFilter.value.businessClientId = null;
        orderFilter.value.advancedSearch = false;
        return;
      }
      router.push({ name: "home" });
    }
    async function showOrderDetails(orderId: number) {
      swal.showLoading();
      var clientHeight = document.getElementById("list-orders");
      orderFilter.value.reservedOrderLastPosition =
        clientHeight?.scrollTop ?? null;
      var apiResultPromise = api.GetOrderDetails(orderId);
      await q.delay(400);
      var apiResult = await apiResultPromise;
      if (apiResult.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: apiResult.errorMessage,
        });
        return;
      }
      if (!apiResult.data) {
        swal.close();
        return;
      }
      if (apiResult.data.customer == null) {
        apiResult.data.customer = new Customer();
      }
      const order: Order = JSON.parse(JSON.stringify(apiResult.data));

      order.products.forEach((product) => {
        const couponsForCurrentProduct = coupons.value.filter(
          (x) => x.productIds.includes(product.id)
        );
        if (product.priceLine.couponId) {
          var coup = couponsForCurrentProduct.find(
            (x) => x.id == product.priceLine.couponId
          );
          if (coup) {
            product.priceLine.coupon = coup;
          }
        }
      });
      order.isReserved = true;
      order.payment = new OrderPayment();
      order.payment.index = apiResult.data.payments.length;
      apiResult.data.payments.forEach(function (item, i) {
        if (item.paymentType == PaymentType.Cash) {
          const payment: CashPayment = {
            documentNumber: item.documentNumber,
            id: item.id,
            amount: item.totalAmount,
            currencyId: item.currencyId,
            index: i,
            localCurrencyRate: item.localCurrencyRate,
            paymentType: item.paymentType,
          };
          order.payment.cashPayments.push(payment);
        } else if (item.paymentType == PaymentType.Check) {
          const payment: CheckPayment = {
            documentNumber: item.documentNumber,
            id: item.id,
            amount: item.totalAmount,
            currencyId: item.currencyId,
            index: i,
            localCurrencyRate: item.localCurrencyRate,
            paymentType: item.paymentType,
            accountNumber: "",
            bankBranchNumber: null,
            bankId: 0,
            checkNumber: 0,
            valueDate: "",
            owner: "",
          };
          order.payment.checkPayments.push(payment);
        } else if (item.paymentType == PaymentType.BankTransfer) {
          const payment: BankTransferPayment = {
            documentNumber: item.documentNumber,
            id: item.id,
            amount: item.totalAmount,
            currencyId: item.currencyId,
            index: i,
            localCurrencyRate: item.localCurrencyRate,
            paymentType: item.paymentType,
            accountNumber: "",
            bankBranchNumber: null,
            bankId: 0,
            valueDate: "",
            receivingAccount: "",
            referenceNumber: 0,
          };
          order.payment.bankTransferPayments.push(payment);
        } else if (item.paymentType == PaymentType.CreditCard) {
          const payment: CreditCardPayment = {
            documentNumber: item.documentNumber,
            id: item.id,
            amount: item.totalAmount,
            currencyId: item.currencyId,
            index: i,
            localCurrencyRate: item.localCurrencyRate,
            paymentType: item.paymentType,
            creditCardPaymentType: 0,
            numberOfPayments: 0,
            transactionId: 0,
          };
          order.payment.creditCardPayments.push(payment);
        }
      });
      var customer = JSON.parse(JSON.stringify(order.customer));
      store.commit("setReservedOrderCustomer", customer);
      delete order["payments"];
      store.commit("setOrder", order);
      store.commit("setOriginOrder", _.cloneDeep(store.state.order));
      store.commit("setReservOrderFilter", orderFilter.value);
      swal.close();

      router.push({
        name: "reservedOrder",
        params: { orderId: order.id },
      });
    }
    function showAll() {
      clearFilter();
      orders.value = [];
      search();
    }
    function advancedSearch() {
      searchWas.value = !searchWas.value;
      orderFilter.value.advancedSearch = true;
    }
    function getClientName(id) {
      return clients.value.find((x) => x.id == id)?.name ?? "";
    }
    onActivated(async () => {
      if (route.params.fromHome) {
        return;
      }
      var apiResultPromise = api.SearchReservedOrders(orderFilter.value, true);
      await q.delay(400);
      var apiResult = await apiResultPromise;
      orders.value = apiResult.data?.orders ?? [];
      var listOrders = document.getElementById("list-orders");
      if (listOrders && orderFilter.value.reservedOrderLastPosition) {
        await q.delay(200);
        listOrders.scrollTop = orderFilter.value.reservedOrderLastPosition;
      }
    });
    const onMountedCall = async () => {
      clients.value = store.state.businessClients;
      const productResult = await api.getAllActiveProducts();
      if(productResult.data){
        products.value = productResult.data;
        swal.close();
      }

      store.commit("clearOrder");
      if (route.params.fromHome) {
        orderFilter.value = new OrderFilter();
      } else {
        searchWas.value = true;
        showNoResult.value = false;
      }
      loaded.value = true;
      const ordersList = document.getElementById("list-orders");
      if (ordersList) {
        ordersList.addEventListener("scroll", handleScroll);
      }

      await nextTick(); // Wait for DOM to update
      if (searchInput.value) {
        searchInput.value.$el.querySelector("input")?.focus(); // Focus the input
      }
    };
    onMounted(onMountedCall);

    const onUnmountedCall = async () => {
      const app = document.getElementById("main-content");
      if (app) {
        app.removeEventListener("scroll", handleScroll);
      }
    };
    onUnmounted(onUnmountedCall);
    return {
      loaded,
      orders,
      debounce: createDebounce(),
      getDateToTitle,
      OrderStatus,
      showOrderDetails,
      searchWas,
      orderFilter,
      clients,
      onClickSearch,
      showNoResult,
      onChangeKeyword,
      back,
      search,
      showAll,
      advancedSearch,
      filterExist,
      getClientName,
      searchBy,
      onClickSearchBy,
      products,
      searchInput
    };
  },
});
